/* Variant picker for the product page, ported from pdpInit in docs/design/maqueta.html (v8).
   Colour and size map to a variant from product.variants; sizes sold out in the chosen colour are
   disabled, and the button is enabled only once an available variant is chosen. Submitting adds to the cart
   with POST /cart/add.js and opens <cart-drawer> (cart-drawer.js); without JS the native /cart/add form works. */

/* Index of the colour option among the product's option names ("Color" or "Colour", any case), or -1. */
export function colorOptionIndex(options) {
  return options.findIndex(name => /^colou?r$/i.test(name.trim()));
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    // connectedCallback runs again if the element is moved in the DOM; bind the listeners once
    if (this.variants) return;
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.sizeIndex = Number(this.dataset.sizeIndex);
    this.colorIndex = colorOptionIndex(JSON.parse(this.dataset.optionNames));
    this.add = this.querySelector("#add");
    this.label = this.querySelector("#addLabel");
    this.idInput = this.querySelector('input[name="id"]');
    this.price = this.querySelector(".price");
    this.status = this.querySelector("[data-status]");
    this.form = this.querySelector("form");
    this.sizeButtons = [...this.querySelectorAll(".sizes button")];
    this.colorButtons = [...this.querySelectorAll(".colors button")];
    this.size = this.sizeButtons.find(b => b.getAttribute("aria-pressed") === "true")?.dataset.size ?? null;
    this.color = this.colorButtons.find(b => b.getAttribute("aria-pressed") === "true")?.dataset.color ?? null;

    this.sizeButtons.forEach(b => b.addEventListener("click", () => {
      this.size = b.dataset.size;
      this.status.textContent = "";
      this.update();
    }));
    this.colorButtons.forEach(b => b.addEventListener("click", () => {
      this.color = b.dataset.color;
      this.status.textContent = "";
      this.colorButtons.forEach(x => x.setAttribute("aria-pressed", x === b));
      this.querySelector("#colorName").textContent = this.color;
      this.update();
    }));
    this.form.addEventListener("submit", e => this.submit(e));
    this.update();
  }

  async submit(e) {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || typeof drawer.render !== "function") return; // the native form takes over
    e.preventDefault();
    if (this.adding || this.add.disabled) return;
    this.adding = true;
    this.add.disabled = true;
    this.status.textContent = "";
    let added = false;
    try {
      let res, data;
      try {
        res = await fetch(this.dataset.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [lineItem(this.idInput.value, new FormData(this.form))], sections: "cart-drawer" }),
        });
        data = await res.json();
      } catch {
        throw new Error(this.dataset.error); // offline, or a reply that is not JSON
      }
      // Shopify explains refusals (sold out meanwhile) in "description"
      if (!res.ok) throw new Error(data.description || data.message || this.dataset.error);
      if (!data.sections?.["cart-drawer"]) throw new Error(this.dataset.error);
      drawer.render(data.sections["cart-drawer"]);
      drawer.open();
      added = true;
    } catch (err) {
      this.status.textContent = err.message;
    } finally {
      this.adding = false;
      this.update();
    }
    if (added) {
      this.label.textContent = this.dataset.added;
      setTimeout(() => { if (!this.adding) this.update(); }, 1800);
    }
  }

  find(size, color) {
    return this.variants.find(v =>
      (this.sizeIndex < 0 || v.options[this.sizeIndex] === size) &&
      (this.colorIndex < 0 || v.options[this.colorIndex] === color));
  }

  update() {
    this.sizeButtons.forEach(b => { b.disabled = !this.find(b.dataset.size, this.color)?.available; });
    // a size picked in another colour that is sold out in this one is let go
    if (this.sizeButtons.some(b => b.dataset.size === this.size && b.disabled)) this.size = null;
    this.sizeButtons.forEach(b => b.setAttribute("aria-pressed", b.dataset.size === this.size));

    // with no size in stock in this colour (or none at all) there is nothing to pick: the button reads sold out
    const anyOpen = this.sizeButtons.some(b => !b.disabled);
    const needsSize = this.sizeIndex >= 0 && this.size === null && anyOpen;
    const variant = needsSize ? null : this.find(this.size, this.color);
    const ok = Boolean(variant?.available);
    this.add.disabled = !ok;
    this.idInput.value = ok ? variant.id : "";
    // the price follows the chosen variant, or the first one in the chosen colour before a size is picked
    const firstSize = (this.sizeButtons.find(b => !b.disabled) ?? this.sizeButtons[0])?.dataset.size ?? null;
    const shown = variant ?? this.find(firstSize, this.color);
    if (shown && this.price && this.dataset.moneyFormat) {
      const pesos = formatMoney(shown.price, this.dataset.moneyFormat);
      this.price.dataset.uyu = shown.price;
      this.price.dataset.pesos = pesos;
      this.price.textContent = pesos;
      document.dispatchEvent(new Event("jc:prices"));
    }
    this.label.textContent = needsSize ? this.dataset.pick : ok ? this.dataset.add : this.dataset.soldOut;
  }
}
if (!customElements.get("product-form")) customElements.define("product-form", ProductForm);

/* The cart line for /cart/add.js: the variant id and the form's line item properties (properties[Name] inputs),
   as the native form would send them. */
export function lineItem(id, formData) {
  const properties = {};
  for (const [name, value] of formData) {
    const m = /^properties\[(.+)\]$/.exec(name);
    if (m) properties[m[1]] = value;
  }
  const item = { id: Number(id), quantity: 1 };
  if (Object.keys(properties).length) item.properties = properties;
  return item;
}

/* Shopify money format (Settings > General), e.g. "$ {{amount_no_decimals_with_comma_separator}}" -> "$ 6.400".
   cents is the variant price in cents, as in product.variants | json. */
function formatMoney(cents, format) {
  const group = (decimals, thousands, decimal) => {
    const [int, frac] = (cents / 100).toFixed(decimals).split(".");
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : "");
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => ({
    amount: group(2, ",", "."),
    amount_no_decimals: group(0, ",", "."),
    amount_with_comma_separator: group(2, ".", ","),
    amount_no_decimals_with_comma_separator: group(0, ".", ","),
    amount_with_apostrophe_separator: group(2, "'", "."),
    amount_with_period_and_space_separator: group(2, " ", "."),
  })[name] ?? group(2, ",", "."));
}

/* Back arrow (spec section 5): it links to the product's category. When the visitor came from a page of
   this site the arrow knows (home, shop, a category, search, the diary, an about or collection page,
   another product), it steps back in history instead, so the browser
   restores the scroll, and it names that page, as backTarget does in the mockup. */
(() => {
  const back = document.querySelector(".back[data-back]");
  const pagesEl = document.getElementById("back-pages");
  if (!back || !pagesEl || !document.referrer) return;
  // a product opened in a new tab has a referrer but nothing to go back to: keep the link.
  // The Navigation API sees only this site's entries; history.length also counts a new tab's about:blank.
  const canGoBack = window.navigation ? navigation.canGoBack : history.length > 1;
  if (!canGoBack) return;
  let from;
  try { from = new URL(document.referrer); } catch { return; }
  if (from.origin !== location.origin || from.pathname === location.pathname) return;
  const trim = p => (p.length > 1 ? p.replace(/\/$/, "") : p);
  const { exact, prefix } = JSON.parse(pagesEl.textContent);
  const path = trim(from.pathname);
  // a whole path first, then the longest prefix that is the path itself or a parent of it
  const parent = Object.keys(prefix).filter(p => path === p || path.startsWith(`${p}/`)).sort((a, b) => b.length - a.length)[0];
  const label = Object.entries(exact).find(([p]) => trim(p) === path)?.[1] ?? prefix[parent];
  if (!label) return;
  const name = back.querySelector("span");
  const categoryLabel = name.textContent;
  back.setAttribute("aria-label", back.getAttribute("aria-label").replace(categoryLabel, label));
  name.textContent = label;
  back.href = from.href;
  back.addEventListener("click", e => { e.preventDefault(); history.back(); });
  // as in the mockup, the category stays reachable above the title when the arrow leads elsewhere
  const category = document.querySelector(".buy [data-category]");
  if (category && label !== categoryLabel) category.hidden = false;
})();
